import * as yup from "yup";

import { Button, Divider, List, ListItem, Typography } from "@mui/material";
import { ContactMailIcon, DateIcon, LocationIcon, TimeIcon } from "../../../components/Icons";
import { InputController, SelectController } from "../../../components/Controllers";
/* eslint-disable max-len */
import React, { useEffect } from "react";
import { fromUnixToArrayDates, fromUnixToFulldateStr, getDateGoogleCalendarFormat, getHourFromUnix } from "../../../utils";

import { createEventIcs } from "../../../libs/ics";
import { useAppointmentStore } from "../../../hooks/redux-hooks/appointment-store/appointment.store";
import { useForm } from "react-hook-form";
import { useModal } from "../../../context/ModalContext";
import { yupResolver } from "@hookform/resolvers/yup";
import { useBookingAppointmentTypeStore, useBookingReasonsNavigation } from "../../../hooks";
import { CREATE_BOOKING_TYPE } from "../../../config/params";
import { useUrgentVisitStore } from "../../../hooks/redux-hooks/urgent-visit-store/urgent-visit.store";

const AddToCalendarSchema = yup.object({
  calendar: yup.string().required("Calendar is required"),
  eventTitle: yup.string().required("Event title is required"),
});

type AddToCalendarFormType = yup.InferType<typeof AddToCalendarSchema>;

const AddToCalendarModal = () => {
  const { closeModal } = useModal();
  const {
    setAppointmentResponse,
    setRescheduleAppointmentsResponse,
    setSelectedPets,
    setReasonForVisit,
    setAppointmentsForm,
    appointmentDetails,
    appointmentsResponse,
    appointmentsRescheduleResponse } = useAppointmentStore();
  const { setBookingAppointmentType } = useBookingAppointmentTypeStore();
  const { resetUrgentVisit } = useUrgentVisitStore();
  const { resetReasonsNavigationStore } = useBookingReasonsNavigation();


  let visit: any;

  if (appointmentsResponse) {
    visit = appointmentsResponse;
  } else if (appointmentsRescheduleResponse) {
    visit = appointmentsRescheduleResponse;
  } else {
    visit = appointmentDetails;
  }
  const { handleSubmit, control, setValue, getValues, formState: { isValid } } = useForm<AddToCalendarFormType>({
    mode: "onChange",
    resolver: yupResolver(AddToCalendarSchema),
    defaultValues: {
      calendar: "",
      eventTitle: "",
    },
  });

  const onOpenCalendar = (data: AddToCalendarFormType) => {
    switch (data.calendar) {
    case "google":
      handleAddToGoogleCalendar();
      break;
    case "ical":
      handleGenerateIcalFile();
      break;
    default:
      console.error("Select calendar");
      break;
    }
  };

  const handleCloseModal = ()=>{
    resetUrgentVisit();
    setSelectedPets([]);
    resetReasonsNavigationStore();
    setReasonForVisit({});
    setAppointmentsForm(null);
    setBookingAppointmentType(CREATE_BOOKING_TYPE.NORMAL_BOOKING);
    setAppointmentResponse(null);
    setRescheduleAppointmentsResponse(null);
    closeModal();
  };
  const handleAddToGoogleCalendar = () => {
    if (!visit) {
      return;
    }


    const googleCalendarLink = [
      "https://calendar.google.com/calendar/render?action=TEMPLATE",
      `&dates=${getDateGoogleCalendarFormat(visit?.start_at, visit?.location.timeZone)}`,
      "%2F",
      `${getDateGoogleCalendarFormat(visit?.start_at + visit.duration, visit.location.timeZone)}`,
      `&details=Good-Vets ${visit.location.locationName}%0AAddress: ${visit.location.address}%0AEmail: ${visit.location.email}%0APhone: ${visit.location.phoneNumber}`,
      `&location=${visit?.location.address}`,
      `&text=${getValues("eventTitle")}`,
    ].join("");

    window.open(googleCalendarLink, "_blank");
  };

  const handleGenerateIcalFile = () => {
    createEventIcs({
      title: getValues("eventTitle"),
      description: visit ? `Good-Vets ${visit.location.locationName}\nAddress: ${visit.location.address}\nEmail: ${visit.location.email}\nPhone: ${visit.location.phoneNumber}` : "",
      location: visit?.location.address,
      start: fromUnixToArrayDates(visit?.start_at),
      duration: {
        hours: Math.floor((visit?.duration || 0) / 3600),
        minutes: ((visit?.duration || 0) % 3600) / 60,
      },
      geo: {
        lat: visit?.location.position.lat as number,
        lon: visit?.location.position.lng as number,
      },
    });
  };

  useEffect(() => {
    if (visit) {
      if (appointmentDetails) {
        const pets = appointmentDetails.pet.name;

        setValue("eventTitle", `${pets} @ Good-Vets ${visit?.location.locationName}`, { shouldValidate: true });
      } else {
        const pets = visit.pets
          .map((pet: any) => pet.name)
          .join(", ");

        setValue("eventTitle", `${pets} @ Good-Vets ${visit?.location.locationName}`, { shouldValidate: true });
      }
    }
  }, [visit]);

  return (
    <form onSubmit={handleSubmit(onOpenCalendar)} className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto scroll-hide p-4 cursor-default">
        <Typography
          variant="h1"
          component="div"
          sx={{ mb: "32px" }}
        >
          Add event
        </Typography>

        <SelectController
          control={control}
          name="calendar"
          label=""
          placeholder="Select calendar"
          options={[
            { value: "google", label: "Google" },
            { value: "ical", label: "iCal file" },
          ]}
        />

        <InputController
          control={control}
          name="eventTitle"
          label=""
          placeholder="Event title"
        />

        {visit && (
          <>
            <List
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                mt: "0",
                mb: "24px",
              }}
            >
              <ListItem sx={{ gap: 2, pl: 0, pr: 0 }}>
                <DateIcon />
                <b style={{ fontFamily: "geograph-regular" }}>{fromUnixToFulldateStr(visit?.start_at)}</b>
              </ListItem>
              <Divider />
              <ListItem sx={{ gap: 2, pl: 0, pr: 0 }}>
                <TimeIcon />
                <b style={{ fontFamily: "geograph-regular" }}>
                  {getHourFromUnix(visit.start_at, visit.location.timeZone)}
                  {" "}-{" "}
                  {getHourFromUnix(visit.end_at, visit.location.timeZone)}
                </b>
              </ListItem>
              <Divider />
              <ListItem sx={{ gap: 2, pl: 0, pr: 0 }}>
                <ContactMailIcon />
                <Typography>
                  <span>{visit?.location.phoneNumber}</span>
                  <br />
                  <span>{visit?.location.email}</span>
                </Typography>
              </ListItem>
              <Divider />
              <ListItem sx={{ gap: 2, pl: 0, pr: 0 }}>
                <LocationIcon />
                <Typography>
                  <b style={{ fontFamily: "geograph-regular" }}>
                    GoodVets {visit?.location.locationName}
                  </b>
                  <br />
                  <span>{visit?.location.address}</span>
                </Typography>
              </ListItem>
            </List>
          </>
        )}


      </div>
      <div className="flex flex-col sticky bottom-1 ">
        <Button
          fullWidth
          variant="text"
          color="secondary"
          onClick={handleCloseModal}
          className="!capitalize !font-bold"
        >
          Cancel
        </Button>
        <Button
          fullWidth
          type="submit"
          id="findAvailabilityButton"
          color="primary"
          disabled={!isValid}
          className="!normal-case !text-sm !font-bold"
        >
          Add to Calendar
        </Button>
      </div>
    </form>
  );
};

export default AddToCalendarModal;
